// API Client for newsnetwide.com backend

const API_BASE = '/api';

class ApiClient {
    constructor(baseUrl = API_BASE) {
        this.baseUrl = baseUrl;
        this.cache = new Map();
        this.cacheTTL = 5 * 60 * 1000; // 5 minutes
    }

    async request(endpoint, options = {}) {
        const url = `${this.baseUrl}${endpoint}`;
        const config = {
            headers: {
                'Content-Type': 'application/json',
                ...options.headers,
            },
            ...options,
        };

        try {
            const response = await fetch(url, config);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || `HTTP ${response.status}`);
            }

            return data;
        } catch (err) {
            console.error(`[api] ${endpoint} failed:`, err);
            throw err;
        }
    }

    // Cache helpers
    getCached(key) {
        const entry = this.cache.get(key);
        if (entry && Date.now() - entry.time < this.cacheTTL) {
            return entry.data;
        }
        return null;
    }

    setCache(key, data) {
        this.cache.set(key, { data, time: Date.now() });
    }

    clearCache(pattern) {
        if (pattern) {
            for (const key of this.cache.keys()) {
                if (key.includes(pattern)) this.cache.delete(key);
            }
        } else {
            this.cache.clear();
        }
    }

    // Countries
    async getCountryStats(params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/countries/stats${query ? `?${query}` : ''}`);
    }

    async getCountries(params = {}) {
        const key = `countries:${JSON.stringify(params)}`;
        const cached = this.getCached(key);
        if (cached) return cached;

        const query = new URLSearchParams(params).toString();
        const data = await this.request(`/countries${query ? `?${query}` : ''}`);
        this.setCache(key, data);
        return data;
    }

    async getCountry(iso2) {
        const key = `country:${iso2}`;
        const cached = this.getCached(key);
        if (cached) return cached;

        const data = await this.request(`/countries/${iso2.toUpperCase()}`);
        this.setCache(key, data);
        return data;
    }

    // News
    async getNews(params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/news${query ? `?${query}` : ''}`);
    }

    async getLatestNews(limit = 10) {
        return this.request(`/news/latest?limit=${limit}`);
    }

    async getBreakingNews(limit = 10) {
        return this.request(`/news/breaking?limit=${limit}`);
    }

    async getNewsByCountry(iso2, params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/news/country/${iso2.toUpperCase()}${query ? `?${query}` : ''}`);
    }

    async getNewsByCategory(slug, params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/news/category/${slug}${query ? `?${query}` : ''}`);
    }

    // Sponsorships
    async getSponsorships(params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/sponsorships${query ? `?${query}` : ''}`);
    }

    async getActiveSponsorships() {
        return this.request('/sponsorships/active');
    }

    async getSponsorshipsForEntity(type, id) {
        return this.request(`/sponsorships/entity/${type}/${id}`);
    }

    // Categories
    async getCategories(params = {}) {
        const query = new URLSearchParams(params).toString();
        return this.request(`/categories${query ? `?${query}` : ''}`);
    }

    async getCategory(slug) {
        return this.request(`/categories/${slug}`);
    }

    // Health
    async healthCheck() {
        return this.request('/health');
    }

    async readinessCheck() {
        return this.request('/ready');
    }

    async getMetrics() {
        return this.request('/metrics');
    }
}

// Singleton instance
export const api = new ApiClient();

export default api;
